import api from 'src/utils/ajax';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';

import { HealthReportWidget, Report, ReportConfig } from './_types';
import {
  denormalizeParams,
  denormalizeReport,
  denormalizeReportConfig,
  denormalizeReportWidget,
  normalizeReport,
  normalizeReportConfig,
  normalizeReportWidget,
} from './normalizers';

const readOnlyFields = [
  'created_at',
  'updated_at',
  'fields',
  'filters',
  'description',
  'customizable',
  'cached_stats',
  'cached_data',
  'name',
];

const POLL_INTERVAL = 2000; // 2 seconds

export async function fetchReport(
  reportType: string,
  params?: Record<string, any>,
  perPage?: number
): Promise<Report> {
  const response = await api.get(`/api/v4/reports/${reportType}`, {
    params: { ...denormalizeParams(params), per: perPage },
  });
  return normalizeReport(response.data);
}

interface FetchReportsOptions {
  favoriteReports?: string[];
}

export const fetchReports = async ({
  favoriteReports = [],
}: FetchReportsOptions): Promise<{
  reports: Report[];
  meta: { total: number };
}> => {
  const response = await api.get('/api/v4/reports?fetch_all=true');

  return {
    reports: response.data.reports.map((report: Report) => ({
      ...normalizeReport(report),
      favorite: (favoriteReports || []).includes(report.id.toString()),
    })),
    meta: response.data.meta,
  };
};

export const fetchReportOptions = async (): Promise<Report[]> => {
  const response = await api.get('/api/v4/reports');
  return response.data.reports.map((report: Report) => ({
    ...normalizeReport(report),
  }));
};

export const fetchHealthReports = async ({
  page,
  perPage: per,
  search,
}): Promise<{ reports: Report[]; total: number }> => {
  const response = await api.get('/api/v3/reports', {
    params: {
      page,
      per,
      'q[name_cont]': search,
    },
  });
  return {
    reports: response.data.report.map((report: Report) => ({
      ...normalizeReport(report),
    })),
    total: response.data.meta.total,
  };
};

export const deleteHealthReport = async (id: string): Promise<void> => {
  await api.delete(`/api/v3/reports/${id}`);
};

export const createHealthReport = async (
  data: Partial<Report>
): Promise<Report> => {
  const response = await api.post('/api/v3/reports', {
    report: denormalizeReport(data as Report),
  });
  return normalizeReport(response.data);
};

export const updateHealthReport = async (
  data: Partial<Report>
): Promise<Report> => {
  const { id, ...rest } = data;
  const response = await api.put(`/api/v3/reports/${id}`, {
    report: denormalizeReport(rest as Report),
  });
  return normalizeReport(response.data);
};

export const fetchHealthReport = async (id: Report['id']): Promise<Report> => {
  const response = await api.get(`/api/v3/reports/${id}`);
  return normalizeReport(response.data);
};

export const fetchReportWidgets = async ({
  reportId,
  fetchAll,
}): Promise<HealthReportWidget[]> => {
  const response = await api.get('/api/v3/report_widgets', {
    params: {
      report_id: reportId,
      fetch_all: fetchAll,
    },
  });

  return response.data.map(normalizeReportWidget);
};

export const createReportWidgets = async (
  data: Partial<HealthReportWidget>
): Promise<HealthReportWidget> => {
  const response = await api.post(`/api/v3/report_widgets`, {
    report_widget: denormalizeReportWidget(data),
  });

  return normalizeReportWidget(response.data);
};

export const updateReportWidgets = async (
  id: HealthReportWidget['id'],
  data: Partial<HealthReportWidget>
): Promise<HealthReportWidget> => {
  const response = await api.put(`/api/v3/report_widgets/${id}`, {
    report_widget: denormalizeReportWidget(data),
  });

  return normalizeReportWidget(response.data);
};

interface RefreshReportWidgetsOptions {
  id: HealthReportWidget['id'];
  refresh?: boolean;
}
export const fetchOrRefreshReportWidgetData = async ({
  id,
  refresh,
}: RefreshReportWidgetsOptions): Promise<
  { success: string } | HealthReportWidget[]
> => {
  const response = await api.get(`/api/v2/report_widgets/${id}/widget_data/`, {
    params: {
      refresh: refresh || undefined,
    },
  });

  if (response.data.success) {
    return response.data;
  }

  return response.data.map(normalizeReportWidget);
};

export const exportHealthReportCsv = async (
  id: Report['id']
): Promise<{ success: boolean }> => {
  const response = await api.post(`/api/v2/reports/${id}/export_csv/`);
  return response.data;
};

export const exportHealthReportPdf = async (url: string) => {
  const res = await fetch(
    'https://us-central1-excom-staging.cloudfunctions.net/screenshot',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: `url=${encodeURIComponent(url)}`,
    }
  );
  return await res.json();
};

export async function updateReport(
  id: string,
  data: Partial<Report>
): Promise<Report> {
  const normalizedData = removeReadOnlyFields(
    normalizeReport(data),
    readOnlyFields
  );

  const response = await api.put(`/api/v4/reports/${id}`, {
    report: normalizedData,
  });

  return normalizeReport(response.data.report);
}

export async function deleteReport(id: string): Promise<void> {
  await api.delete(`/api/v4/reports/${id}`);
}

export async function refreshReport(
  reportType: string,
  params?: Record<string, any>,
  refreshParams?: Record<string, any>,
  perPage?: number
): Promise<Report> {
  let report = await fetchReport(
    reportType,
    {
      ...(params ? denormalizeParams(params) : {}),
      ...(!refreshParams || Object.keys(refreshParams).length === 0
        ? {}
        : refreshParams),
    },
    perPage
  );

  while (report.refreshing) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    report = await fetchReport(reportType, params, perPage);
  }
  return report;
}

export async function exportReport(
  reportType: string,
  params?: URLSearchParams
): Promise<string> {
  // Initial request to start export
  await api.get(`/api/v4/reports/${reportType}.csv`, {
    params: denormalizeParams(params),
  });
  // Poll until report is done generating
  let notification;
  const time = Math.floor(Date.now() / 1000);
  do {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    notification = await api.get('/api/v3/users/waiting_for_report', {
      params: {
        type: reportType,
        time: time,
        react: true,
      },
    });
  } while (notification.status === 204);
  return notification.data.url;
}

export async function getReportConfig(
  reportType: string
): Promise<ReportConfig> {
  const response = await api.get(`/api/v4/reports/${reportType}/report_config`);
  return normalizeReportConfig(response.data);
}

export async function saveReportConfig(
  reportType: string,
  data: Partial<ReportConfig>
): Promise<ReportConfig> {
  const response = await api.post(
    `/api/v4/reports/${reportType}/save_report_config`,
    denormalizeReportConfig(data as ReportConfig)
  );
  return normalizeReportConfig(response.data);
}

/**
 * Calculates summary statistics for a column in report data
 * @param key The column key
 * @param array The report data array
 * @param currency Currency code for formatting monetary values
 * @returns A formatted string with the summary statistic in parentheses
 */
export function columnSummary(
  key: string,
  array: any[],
  currency: string
): string {
  const dollarExcludeColumns = ['Value', 'Description'];
  const dollarIncludeColumns = [
    'Amount Owed',
    'Total Package Amount Paid By Account',
    'Gross',
    'Fee',
    'Net',
    'Net Before Refunds',
    'Amount Refunded',
    'Fee Adjustment',
    'Cash Revenue',
    'Check Revenue',
    'Other Revenue',
    'Total Clients',
    'Net Value Per Client',
  ];

  if (
    !dollarExcludeColumns.includes(key) &&
    (key.toLowerCase().includes('revenue') ||
      dollarIncludeColumns.includes(key))
  ) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    });
    let sum = 0.0;
    (array || []).forEach((object) => {
      const value = object[key];
      if (value) {
        const number = parseFloat(String(value).replace(/[^0-9.-]/g, '')) * 100;
        if (number) sum += number;
      }
    });
    return sum > 0 ? `(${formatter.format(sum / 100)})` : '';
  }

  if (key.toLowerCase().includes('most')) {
    let max = 0;
    (array || []).forEach((object) => {
      const value = object[key];
      if (value && typeof value !== 'string') {
        max = Math.max(max, value);
      }
    });
    return max > 0 ? `(${max.toString()})` : '';
  }

  let sum = 0;
  let numOfValues = 0;
  (array || []).forEach((object) => {
    const value = object[key];
    if ((value || value === 0) && typeof value !== 'string') {
      sum += value;
      numOfValues += 1;
    }
  });

  if (key.toLowerCase().includes('average')) {
    sum = parseFloat((sum / numOfValues).toFixed(2));
    return sum > 0 ? `(${sum.toString()})` : '';
  } else if (key.includes('%')) {
    sum = parseFloat((sum / numOfValues).toFixed(2)) * 100;
    return sum > 0 ? `(${sum.toString()}%)` : '';
  } else if (key.toLowerCase().includes('total')) {
    return sum > 0 ? `(${sum.toString()})` : '';
  }

  return '';
}
